import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { SearchVcsIntegrationsOutput, VcsIntegration, VcsProvider } from "types/generated";
import { uniqByKey } from "utils/uniq";

import { VcsListProps } from ".";
import { INITIAL_SORT_DIRECTION, INITIAL_SORT_OPTION } from "./Table/constants";
import { ITEMS_LIMIT } from "./constants";
import { SEARCH_VCS_INTEGRATIONS } from "./gql";

const useSearchVcsIntegrations = (
  providers: VcsProvider[] | null = null
): Omit<VcsListProps, "provider"> => {
  const { onError } = useTypedContext(FlashContext);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(urlParams, INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION),
    [urlParams]
  );

  const { data, error, loading, networkStatus, fetchMore, refetch } = usePolledQuery<{
    providers: VcsProvider[];
    searchVCSIntegrations: SearchVcsIntegrationsOutput;
  }>(SEARCH_VCS_INTEGRATIONS, {
    variables: {
      input: {
        predicates: [
          {
            exclude: null,
            field: "provider",
            constraint: {
              booleanEquals: null,
              enumEquals: providers,
              hierarchyNodeValueEquals: null,
              stringEquals: null,
              stringMatches: null,
              timeInLast: null,
              timeInRange: null,
            },
          },
        ],
        orderBy: sortOptionFields,
        fullTextSearch: searchInput,
        first: ITEMS_LIMIT,
        after: null,
      },
      searchUsersSuggestionsInput: {
        fields: null,
        fullTextSearch: null,
        predicates: null,
      },
    },
    pollingGroup: PollingIntervalGroups.Lists,
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
  });

  const integrations = useMemo(() => {
    return (data?.searchVCSIntegrations?.edges || []).reduce<VcsIntegration[]>(
      (acc, next) => (next?.node ? [...acc, next.node] : acc),
      []
    );
  }, [data?.searchVCSIntegrations?.edges]);

  const loadMoreItems = useCallback(async () => {
    try {
      await fetchMore({
        updateQuery: (prev, { fetchMoreResult }) => {
          return {
            providers: [...prev.providers, ...(fetchMoreResult?.providers || [])],
            searchVCSIntegrations: {
              ...prev.searchVCSIntegrations,
              ...fetchMoreResult?.searchVCSIntegrations,
              edges: uniqByKey(
                [
                  ...(prev.searchVCSIntegrations?.edges || []),
                  ...(fetchMoreResult?.searchVCSIntegrations?.edges || []),
                ],
                "cursor"
              ),
            },
          };
        },
        variables: {
          input: {
            first: ITEMS_LIMIT,
            after: data?.searchVCSIntegrations?.pageInfo?.endCursor,
            fullTextSearch: searchInput,
            orderBy: sortOptionFields,
            predicates: [
              {
                exclude: null,
                field: "provider",
                constraint: {
                  booleanEquals: null,
                  enumEquals: providers,
                  hierarchyNodeValueEquals: null,
                  stringEquals: null,
                  stringMatches: null,
                  timeInLast: null,
                  timeInRange: null,
                },
              },
            ],
            skip:
              !data?.searchVCSIntegrations?.pageInfo?.endCursor ||
              !data?.searchVCSIntegrations?.pageInfo?.hasNextPage,
          },
        },
      });
    } catch (error) {
      onError(error);
    }
  }, [data, fetchMore, onError, searchInput, sortOptionFields, providers]);

  return {
    integrations,
    loadMoreItems,
    refetchIntegrations: refetch,
    isPageLoading:
      loading && !data?.searchVCSIntegrations && networkStatus === NetworkStatus.loading,
    hasNoResults: !error && !!searchInput && integrations.length === 0,
    isPageNotFound: !loading && !error && !data?.searchVCSIntegrations,
    hasNextPage: !!data?.searchVCSIntegrations?.pageInfo?.hasNextPage,
    error,
  };
};

export default useSearchVcsIntegrations;
